import { AppSessionId, OutputContentId } from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { AppSessionMpFragment } from "../../appsession-multiplayer/AppSessionMPModel.generated";
import { RootState } from "../store";

export enum OutputContentLoadStatus {
  NO_CONTENT = "NO_CONTENT",
  LOADING = "LOADING",
  READY = "READY",
  FAILED = "FAILED",
}

export type OutputContentValue = {
  id: OutputContentId;
} & (
  | {
      status:
        | OutputContentLoadStatus.NO_CONTENT
        | OutputContentLoadStatus.LOADING
        | OutputContentLoadStatus.FAILED;
      contents: undefined;
    }
  | {
      status: OutputContentLoadStatus.READY;
      contents: string;
    }
);

export interface OutputContentAppSessionValue {
  numberOfSubscribers: number;
  outputContents: Record<OutputContentId, OutputContentValue | undefined>;
}

const makeInitialAppSessionValue = (): OutputContentAppSessionValue => ({
  numberOfSubscribers: 0,
  outputContents: {},
});

export interface OutputContentState {
  [appSessionId: AppSessionId]: OutputContentAppSessionValue | undefined;
}

const initialOutputContentState: OutputContentState = {};

const outputContentSlice = createSlice({
  name: "outputContent",
  initialState: initialOutputContentState,
  reducers: {
    subscribe: (
      state,
      {
        payload: { appSessionId },
      }: PayloadAction<{ appSessionId: AppSessionId }>,
    ) => {
      const prev = (state[appSessionId] ??= makeInitialAppSessionValue());
      prev.numberOfSubscribers++;
    },
    unsubscribe: (
      state,
      {
        payload: { appSessionId },
      }: PayloadAction<{ appSessionId: AppSessionId }>,
    ) => {
      const prev = state[appSessionId];
      if (prev != null) {
        prev.numberOfSubscribers--;
        // evict if there are no more subscribers
        if (prev.numberOfSubscribers === 0) {
          delete state[appSessionId];
        }
      }
    },
    setValues: (
      state,
      {
        payload: { appSessionId, values },
      }: PayloadAction<{
        appSessionId: AppSessionId;
        values: OutputContentValue[];
      }>,
    ) => {
      const prev = (state[appSessionId] ??= makeInitialAppSessionValue());
      for (const value of values) {
        prev.outputContents[value.id] = value;
      }
    },
    deleteValues: (
      state,
      {
        payload: { appSessionId, outputContentIds },
      }: PayloadAction<{
        appSessionId: AppSessionId;
        outputContentIds: OutputContentId[];
      }>,
    ) => {
      const prev = (state[appSessionId] ??= makeInitialAppSessionValue());
      for (const outputContentId of outputContentIds) {
        delete prev.outputContents[outputContentId];
      }
    },
    initializeFromAppSessionMPData: (
      state,
      {
        payload: { appSessionId, data },
      }: PayloadAction<{
        appSessionId: AppSessionId;
        data: AppSessionMpFragment;
      }>,
    ) => {
      const prev = (state[appSessionId] ??= makeInitialAppSessionValue());

      const allOutputContents = data.appSessionCells.flatMap((a) =>
        a.outputs.flatMap((o) => o.frontendOutputContents),
      );

      for (const outputContent of allOutputContents) {
        prev.outputContents[outputContent.id] = outputContent.requiresLazyLoad
          ? {
              id: outputContent.id,
              status: OutputContentLoadStatus.NO_CONTENT,
              contents: undefined,
            }
          : {
              id: outputContent.id,
              status: OutputContentLoadStatus.READY,
              contents: outputContent.contents,
            };
      }
    },
  },
});

export const outputContentReducer = outputContentSlice.reducer;

export const outputContentActions = outputContentSlice.actions;

export const outputContentSelectors = {
  select: (state: RootState) => state.outputContent,
  selectAppSessionValue: (state: RootState, appSessionId: AppSessionId) =>
    outputContentSelectors.select(state)[appSessionId],
  selectAppSessionValueOrThrow: (
    state: RootState,
    appSessionId: AppSessionId,
  ) => {
    const appSessionValue = outputContentSelectors.selectAppSessionValue(
      state,
      appSessionId,
    );

    if (appSessionValue == null) {
      throw new Error(
        `Output content app session value not found for app session ${appSessionId}`,
      );
    } else {
      return appSessionValue;
    }
  },
  selectValue: (
    state: RootState,
    appSessionId: AppSessionId,
    outputContentId: OutputContentId,
  ) => {
    const appSessionValue = outputContentSelectors.selectAppSessionValue(
      state,
      appSessionId,
    );

    return appSessionValue?.outputContents[outputContentId];
  },
  selectValueOrThrow: (
    state: RootState,
    appSessionId: AppSessionId,
    outputContentId: OutputContentId,
  ) => {
    const appSessionValue = outputContentSelectors.selectAppSessionValueOrThrow(
      state,
      appSessionId,
    );

    const value = appSessionValue?.outputContents[outputContentId];

    if (value == null) {
      throw new Error(
        `Output content value not found for output content ${outputContentId}`,
      );
    } else {
      return value;
    }
  },
};
